import type { IPOI } from '@/shared/api/types/poi-types'
import type { UpdateTourPayload } from '@/shared/api/types/tour-types'
import type {
  TourEditSection,
  TourFormState,
  TourValidationError,
} from '@/features/tour/tour-form-model'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateTourMutation, useUpdateTourMutation } from '@/entities/tour'
import {
  buildBasicInfoUpdatePayload,
  buildCreateTourPayload,
  normalizeTourTranslations,
  resolveRouteCoordinates,
  validateRouteCoordinates,
  validateTicketLink,
  validateTourImages,
  validateTourScope,
  validateTourSubmission,
  validateTourTranslations,
} from '@/features/tour/tour-form-model'
import { normalizeTourCoordinates } from '@/features/tour/tour-route-map-utils'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { generatePath } from '@/shared/libs/generate-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'

interface UseTourFormActionsParams {
  tenantId: string
  tourId: string | undefined
  isEdit: boolean
  formState: TourFormState
  poiMap: Record<string, IPOI>
  isActive: boolean
  /** Banner or thumbnail is still uploading. */
  isMediaUploading: boolean
  clearCurrentJobs: () => void
  /** Lets the next route change through the unsaved-changes guard (after a successful save). */
  allowNextNavigation: () => void
  commitBaseline: {
    translations: () => void
    basicInfo: () => void
    coordinates: (saved: TourFormState['routeCoordinates']) => void
    media: () => void
  }
}

/** Create/update flows of the tour form: whole-form create on one side, independent per-section saves on the edit page. */
export function useTourFormActions({
  tenantId,
  tourId,
  isEdit,
  formState,
  poiMap,
  isActive,
  isMediaUploading,
  clearCurrentJobs,
  allowNextNavigation,
  commitBaseline,
}: UseTourFormActionsParams) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const createMutation = useCreateTourMutation()
  const updateMutation = useUpdateTourMutation()
  const [savingSection, setSavingSection] = useState<TourEditSection | null>(null)

  const { basicInfo, translations, selectedPois, routeCoordinates, thumbnail, banner } = formState
  const listPath = scopedAdminPath(generatePath('/tenant/:tenantId/tours', { tenantId }))

  function showValidationError(error: TourValidationError) {
    showToast.error(
      error.defaultValue ? t(error.key, { defaultValue: error.defaultValue }) : t(error.key),
    )
  }

  function navigateToList() {
    void navigate({ href: listPath })
  }

  /** "Create destination" sends the admin away and brings them back to this exact form afterwards. */
  function handleCreateDestinationNavigation() {
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : listPath
    const destinationsPath = scopedAdminPath(
      generatePath('/tenant/:tenantId/destinations', { tenantId }),
    )
    allowNextNavigation()
    void navigate({ href: `${destinationsPath}?returnTo=${encodeURIComponent(returnTo)}` })
  }

  /**
   * Create flow: validate the whole form, build the API payload, create the tour and go back to the list.
   * (Editing never goes through here — each edit section has its own save below.)
   */
  async function handleSubmit() {
    if (isEdit) return

    const normalizedTranslations = normalizeTourTranslations(translations)
    const coordinates = resolveRouteCoordinates(routeCoordinates, selectedPois, poiMap)
    const validationError = validateTourSubmission({
      isUploading: isMediaUploading,
      translations: normalizedTranslations,
      basicInfo,
      ...(banner !== undefined ? { banner } : {}),
      ...(thumbnail !== undefined ? { thumbnail } : {}),
      coordinates,
    })
    if (validationError) {
      showValidationError(validationError)
      return
    }

    try {
      await createMutation.mutateAsync(
        buildCreateTourPayload({
          basicInfo,
          translations: normalizedTranslations,
          selectedPois,
          coordinates,
          banner: banner?.trim() ?? '',
          thumbnail: thumbnail?.trim() ?? '',
          isActive,
        }),
      )
      clearCurrentJobs()
      showToast.success(t('tour.createSuccess'))
      allowNextNavigation()
      await navigate({ href: listPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  /** Shared wrapper of the edit-page section saves: loading flag, success/error toast; resolves to `true` when saved. */
  async function saveEditSection(
    section: TourEditSection,
    payload: UpdateTourPayload,
  ): Promise<boolean> {
    if (!isEdit || !tourId) {
      return false
    }
    try {
      setSavingSection(section)
      await updateMutation.mutateAsync({ id: tourId, payload })
      showToast.success(t('common.saved', { defaultValue: 'Đã lưu.' }))
      return true
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return false
    } finally {
      setSavingSection(null)
    }
  }

  // * VI: Chỉ cập nhật baseline khi API thành công, để dirty-check luôn phản ánh đúng dữ liệu đã lưu. | EN: Baselines are updated only after the API succeeded, so dirty checks always reflect what is really saved.
  async function handleSaveTranslations() {
    const normalizedTranslations = normalizeTourTranslations(translations)
    const validationError = validateTourTranslations(normalizedTranslations)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (await saveEditSection('translations', { localizedData: normalizedTranslations })) {
      commitBaseline.translations()
    }
  }

  /** Saves duration/destination/ticket link/favourite only — route coordinates have their own button. */
  async function handleSaveBasicInfo() {
    const validationError = validateTourScope(basicInfo) ?? validateTicketLink(basicInfo.ticketLink)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (await saveEditSection('basicInfo', buildBasicInfoUpdatePayload(basicInfo))) {
      commitBaseline.basicInfo()
    }
  }

  /** Saves the route on its own so the admin can fix the path without resubmitting other fields. */
  async function handleSaveCoordinates() {
    const coordinates = resolveRouteCoordinates(routeCoordinates, selectedPois, poiMap)
    const validationError = validateRouteCoordinates(coordinates)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (await saveEditSection('coordinates', { coordinates })) {
      commitBaseline.coordinates(normalizeTourCoordinates(coordinates))
    }
  }

  /** Saves banner/thumbnail; blocked while an upload is running or an image is missing. */
  async function handleSaveMedia() {
    if (!isEdit || !tourId) {
      return
    }
    if (isMediaUploading) {
      showToast.error(t('tour.waitMediaUpload'))
      return
    }
    const validationError = validateTourImages(banner, thumbnail)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    const saved = await saveEditSection('media', {
      ...(thumbnail !== undefined ? { thumbnail } : {}),
      ...(banner !== undefined ? { banner } : {}),
    })
    if (saved) {
      commitBaseline.media()
    }
  }

  return {
    savingSection,
    isUpdating: updateMutation.isPending,
    isSaving: createMutation.isPending || updateMutation.isPending || isMediaUploading,
    navigateToList,
    handleCreateDestinationNavigation,
    handleSubmit,
    handleSaveTranslations,
    handleSaveBasicInfo,
    handleSaveCoordinates,
    handleSaveMedia,
  }
}
